import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput, formatFullPhone } from '@/components/ui/phone-input'
import { ArrowLeft } from 'lucide-react'
import type { PageProps } from '@/types'

const COLORS = [
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899', '#f43f5e',
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6b7280',
]

interface BusinessOption {
  id: number
  name: string
}

interface InviteFormProps extends PageProps {
  errors?: Record<string, string>
  businesses?: BusinessOption[]
}

export default function InviteForm({
  errors = {},
  businesses = [],
  current_user,
  current_organization,
  current_business,
}: InviteFormProps) {
  const orgSlug = current_organization?.slug || ''

  const [processing, setProcessing] = useState(false)
  const [invitedName, setInvitedName] = useState('')
  const [countryCode, setCountryCode] = useState('+52')
  const [localPhone, setLocalPhone] = useState('')
  const [role, setRole] = useState('staff')
  const [color, setColor] = useState(COLORS[Math.floor(Math.random() * COLORS.length)])
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<number[]>(
    current_business ? [current_business.id] : businesses.map(b => b.id)
  )

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    router.post(`/${orgSlug}/team`, {
      staff_assignment: {
        invited_name: invitedName,
        invited_phone: formatFullPhone(countryCode, localPhone),
        role,
        color,
        business_ids: selectedBusinessIds,
      }
    }, {
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/team`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al equipo
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            Invitar miembro
          </h1>
          <p className="mt-1 text-muted-foreground">
            Crea una invitación y comparte el link por WhatsApp
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                placeholder="María García"
                value={invitedName}
                onChange={(e) => setInvitedName(e.target.value)}
                required
              />
              {errors.invited_name && (
                <p className="text-sm text-destructive">{errors.invited_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
              <PhoneInput
                countryCode={countryCode}
                localPhone={localPhone}
                onCountryCodeChange={setCountryCode}
                onLocalPhoneChange={setLocalPhone}
              />
              {errors.invited_phone && (
                <p className="text-sm text-destructive">{errors.invited_phone}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Compartirás el link de invitación por este número
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Rol</Label>
              <select
                id="role"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="staff">Personal - Solo ve el calendario</option>
                <option value="admin">Administrador - Acceso completo</option>
              </select>
              {errors.role && (
                <p className="text-sm text-destructive">{errors.role}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Color en calendario</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`h-8 w-8 rounded-full transition-all ${
                      color === c
                        ? 'ring-2 ring-ring ring-offset-2 ring-offset-background'
                        : 'hover:scale-110'
                    }`}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>

          {businesses.length > 1 && (
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div className="space-y-2">
                <Label>¿En qué sucursales atiende?</Label>
                <p className="text-xs text-muted-foreground">
                  Selecciona las sucursales donde trabajará este miembro
                </p>
                <div className="space-y-2 mt-2">
                  {businesses.map((biz) => (
                    <label key={biz.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBusinessIds.includes(biz.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBusinessIds([...selectedBusinessIds, biz.id])
                          } else {
                            setSelectedBusinessIds(selectedBusinessIds.filter(id => id !== biz.id))
                          }
                        }}
                        className="h-4 w-4 rounded border-input"
                      />
                      <span className="text-sm">{biz.name}</span>
                    </label>
                  ))}
                </div>
                {selectedBusinessIds.length === 0 && (
                  <p className="text-sm text-destructive">Selecciona al menos una sucursal</p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={processing || selectedBusinessIds.length === 0}>
              {processing ? 'Creando...' : 'Crear invitación'}
            </Button>
            <Link href={`/${orgSlug}/team`}>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
